/**
 * Headset harness — Phase 0.2 / 0.3 of the plan, with the test battery.
 *
 * A bare IWSDK world loads a published bundle into one batched mesh per
 * system and measures what the design needs measured on a real Quest: the
 * extensions present, the stereo render path the session took, draw calls,
 * triangles and frame times per test case, and the BatchedMesh flicker hazard
 * (default compaction under visibility churn) against the stable-slot batch.
 *
 * The battery runs once in 2D on load (with canvas snapshots per case) and
 * again when the tester enters VR. When VR ends, the tester records what they
 * saw and the record is POSTed to /api/results, where the testing agent
 * collects it (scripts/collect.mjs). Everything also goes to the overlay, to
 * a HUD that follows the head in XR, and to the console as `[Harness]` lines.
 */
import { SessionMode, World, createSystem } from "@iwsdk/core";
import { CanvasTexture, Color, MeshStandardMaterial, REVISION, Sprite, SpriteMaterial, type WebGLRenderer } from "three";
import { browserPorts, buildBatchedAtlas, loadLevelChunks, loadManifest, type BatchedAtlas, type Manifest, type SystemRecord } from "@atlas/runtime";
import { PLANS, type CaseSpec } from "./plan.js";
import { loadLocal, newId, saveLocal, submit, summarise, type CaseResult, type DeviceFacts, type Observations, type PassResult, type ResultRecord, type SessionFacts } from "./results.js";

// --- configuration from the URL -------------------------------------------
const params = new URLSearchParams(location.search);
const bundleBase = params.get("bundle") ?? (import.meta.env.DEV ? "/data/publish/latest" : "./atlas/latest");
const manual: CaseSpec = {
  id: "manual",
  title: "manual",
  level: params.get("level") ?? "far",
  batch: params.get("batch") === "default" ? "default" : "stable",
  churn: params.get("churn") === "1",
  cull: params.get("cull") !== "0",
  systems: params.get("systems")?.split(",").filter(Boolean),
  seconds: 0,
  watch: "",
};
const planName = params.get("battery") ?? "none";
const autoStart = params.get("auto") === "1";

// --- DOM ------------------------------------------------------------------
const overlay = document.getElementById("overlay")!;
const statusEl = document.getElementById("status")!;
const formEl = document.getElementById("form")!;
const lines: string[] = [];
const log: string[] = [];
function say(line: string): void {
  lines.push(line);
  if (lines.length > 12) lines.shift();
  overlay.textContent = lines.join("\n");
  log.push(`${new Date().toISOString().slice(11, 23)} ${line}`);
  if (log.length > 400) log.shift();
  console.info(`[Harness] ${line}`);
}

interface XrInternals {
  isMultiview?: boolean;
  isPresenting: boolean;
  getSession(): (XRSession & { enabledFeatures?: readonly string[] }) | null;
  getCamera(): { cameras?: unknown[] };
  addEventListener(type: string, listener: () => void): void;
}

// --- frame statistics -------------------------------------------------------
interface FrameStats {
  frames: number;
  renders: number;
  window: number[];
  calls: number;
  triangles: number;
  culled: number;
  churnCursor: number;
  /** Frames collected for the current battery case, or null when not measuring. */
  measuring: number[] | null;
  captureRequested: boolean;
  lastSnapshot: string | null;
}
const stats: FrameStats = { frames: 0, renders: 0, window: [], calls: 0, triangles: 0, culled: 0, churnCursor: 0, measuring: null, captureRequested: false, lastSnapshot: null };

let world: World | null = null;
let manifest: Manifest | null = null;
let atlas: BatchedAtlas | null = null;
let active: CaseSpec = manual;
let session: SessionFacts = { presenting: false, isMultiview: null, layersGranted: null, features: [], views: null };
const chunkCache = new Map<string, Map<number, ArrayBuffer>>();
const ports = browserPorts();

let hudSprite: Sprite | null = null;
let hudCanvas: HTMLCanvasElement | null = null;

function makeHud(): Sprite {
  hudCanvas = document.createElement("canvas");
  hudCanvas.width = 768;
  hudCanvas.height = 256;
  const texture = new CanvasTexture(hudCanvas);
  const sprite = new Sprite(new SpriteMaterial({ map: texture, depthTest: false, transparent: true }));
  sprite.scale.set(0.45, 0.15, 1);
  sprite.position.set(0, -0.18, -0.7);
  sprite.renderOrder = 1000;
  return sprite;
}

function drawHud(text: string[]): void {
  if (!hudCanvas || !hudSprite) return;
  const ctx = hudCanvas.getContext("2d")!;
  ctx.clearRect(0, 0, hudCanvas.width, hudCanvas.height);
  ctx.fillStyle = "rgba(0,0,0,0.6)";
  ctx.fillRect(0, 0, hudCanvas.width, hudCanvas.height);
  ctx.fillStyle = "#e8e8e4";
  ctx.font = "26px system-ui, sans-serif";
  text.forEach((t, i) => ctx.fillText(t, 16, 38 + i * 34));
  (hudSprite.material as SpriteMaterial).map!.needsUpdate = true;
}

/** Capture draw counts right after every render: the only reliable place, whatever the host's loop order. */
function hookRenderer(renderer: WebGLRenderer): void {
  const original = renderer.render.bind(renderer);
  renderer.render = ((scene, camera) => {
    original(scene, camera);
    stats.calls = renderer.info.render.calls;
    stats.triangles = renderer.info.render.triangles;
    stats.renders += 1;
    if (stats.captureRequested && !renderer.xr.isPresenting) {
      // same frame as the draw, before the buffer is cleared: the only moment toDataURL sees the picture
      stats.captureRequested = false;
      try {
        stats.lastSnapshot = renderer.domElement.toDataURL("image/jpeg", 0.72);
      } catch (error) {
        say(`snapshot failed: ${error instanceof Error ? error.message : String(error)}`);
        stats.lastSnapshot = null;
      }
    }
  }) as WebGLRenderer["render"];
}

class StatsSystem extends createSystem({}) {
  private lastLog = 0;
  update(delta: number): void {
    const ms = delta * 1000;
    stats.frames += 1;
    stats.window.push(ms);
    if (stats.window.length > 180) stats.window.shift();
    if (stats.measuring) stats.measuring.push(ms);
    if (atlas) {
      stats.culled = atlas.stats().culled;
      if (active.churn) {
        // hide a moving window of instances each frame (50 on the atlas, a quarter of a small bundle)
        // so the draw list changes every frame
        const ids = [...atlas.parts.keys()];
        const width = Math.max(1, Math.min(50, Math.floor(ids.length / 4)));
        for (let k = 0; k < width; k++) atlas.setVisible(ids[(stats.churnCursor + k) % ids.length]!, true);
        stats.churnCursor = (stats.churnCursor + width) % ids.length;
        for (let k = 0; k < width; k++) atlas.setVisible(ids[(stats.churnCursor + k) % ids.length]!, false);
      }
    }
    const now = performance.now();
    if (now - this.lastLog > 2000) {
      this.lastLog = now;
      const s = summarise(stats.window);
      const text = [
        `${active.id}: ${active.level} · ${active.batch} batches${active.churn ? " · churn" : ""}${active.cull ? "" : " · no cull"}`,
        `draws ${stats.calls} · tris ${stats.triangles.toLocaleString()} · culled ${stats.culled}`,
        `frame avg ${s.avgMs.toFixed(2)} ms · p95 ${s.p95Ms.toFixed(2)} · max ${s.maxMs.toFixed(2)} · ${s.fps.toFixed(0)} fps`,
        `multiview ${String(session.isMultiview ?? "n/a")} · layers ${String(session.layersGranted ?? "n/a")} · presenting ${session.presenting}${battery ? ` · battery ${battery.status()}` : ""}`,
      ];
      drawHud(text);
      say(text.join(" | "));
    }
  }
}

// --- scene ------------------------------------------------------------------
async function chunksFor(level: string): Promise<Map<number, ArrayBuffer>> {
  let cached = chunkCache.get(level);
  if (!cached) {
    cached = await loadLevelChunks(bundleBase, manifest!, level, ports, (n, t) => (statusEl.textContent = `chunks ${level} ${n}/${t}`));
    chunkCache.set(level, cached);
  }
  return cached;
}

async function applyCase(spec: CaseSpec): Promise<void> {
  if (!world || !manifest) throw new Error("world not ready");
  if (!manifest.levels.some((l) => l.id === spec.level)) throw new Error(`level ${spec.level} not in bundle`);
  const chunks = await chunksFor(spec.level);
  if (atlas) {
    world.scene.remove(atlas.root);
    for (const mesh of atlas.meshes.values()) {
      mesh.geometry.dispose();
      (mesh.material as MeshStandardMaterial).dispose();
    }
  }
  const wanted = spec.systems ?? manifest.systems.filter((s) => s.defaultVisible).map((s) => s.id);
  atlas = buildBatchedAtlas(manifest, chunks, {
    level: spec.level,
    systems: wanted,
    stable: spec.batch === "stable",
    materialFor: (system: SystemRecord) =>
      new MeshStandardMaterial({ color: 0xffffff, roughness: 0.8, ...(system.material === "translucent" ? { transparent: true, opacity: 0.35, depthWrite: false } : {}) }),
  });
  for (const mesh of atlas.meshes.values()) {
    if (!spec.cull && "cullPerFrame" in mesh) (mesh as unknown as { cullPerFrame: boolean }).cullPerFrame = false;
  }
  // life size, 1.2 m in front of the player, facing them
  atlas.root.position.set(0, 0, -1.2);
  atlas.root.rotation.y = Math.PI;
  world.scene.add(atlas.root);
  stats.churnCursor = 0;
  active = spec;
  const s = atlas.stats();
  say(`case ${spec.id}: ${spec.level} · ${spec.batch}${spec.churn ? " · churn" : ""}${spec.cull ? "" : " · no cull"} · ${s.systems} batches · ${s.instances} parts · ${s.triangles.toLocaleString()} tris`);
}

function deviceFacts(renderer: WebGLRenderer): DeviceFacts {
  const ua = navigator.userAgent;
  return {
    userAgent: ua,
    looksLikeQuest: /OculusBrowser|Quest|Pacific|Horizon/i.test(ua),
    webgl2: renderer.capabilities.isWebGL2,
    multiDraw: renderer.extensions.has("WEBGL_multi_draw"),
    multiviewExtension: renderer.extensions.has("OCULUS_multiview"),
    threeRevision: REVISION,
    screen: [screen.width, screen.height],
    devicePixelRatio: devicePixelRatio,
  };
}

// --- the battery -------------------------------------------------------------
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

class Battery {
  readonly record: ResultRecord;
  private running = false;
  private current: PassResult | null = null;
  private caseIndex = 0;
  private xrDone = false;

  constructor(readonly plan: string, readonly cases: CaseSpec[], renderer: WebGLRenderer) {
    this.record = {
      id: newId(),
      createdAt: new Date().toISOString(),
      bundle: { id: manifest?.id ?? "?", run: manifest?.provenance.run ?? "?", base: bundleBase },
      plan,
      device: deviceFacts(renderer),
      passes: [],
      snapshots: {},
      log: [],
      observations: null,
      submitted: { posted: false, stored: false, at: null, error: null },
    };
  }

  status(): string {
    if (!this.current) return this.xrDone ? "done" : "idle";
    return `${this.current.mode} ${this.caseIndex + 1}/${this.cases.length}`;
  }

  async run(mode: "2d" | "xr"): Promise<PassResult> {
    if (this.running) throw new Error("battery already running");
    this.running = true;
    const pass: PassResult = { mode, startedAt: new Date().toISOString(), finishedAt: "", cases: [] };
    this.current = pass;
    say(`battery ${this.plan} (${mode}): ${this.cases.length} cases`);
    try {
      for (this.caseIndex = 0; this.caseIndex < this.cases.length; this.caseIndex++) {
        const spec = this.cases[this.caseIndex]!;
        if (mode === "xr" && !session.presenting) {
          say("XR session ended: battery stopped");
          break;
        }
        statusEl.textContent = `${mode} ${this.caseIndex + 1}/${this.cases.length}: ${spec.title} — ${spec.watch}`;
        try {
          await applyCase(spec);
        } catch (error) {
          say(`case ${spec.id} skipped: ${error instanceof Error ? error.message : String(error)}`);
          continue;
        }
        await sleep(1000);
        stats.measuring = [];
        const startedAt = new Date().toISOString();
        await sleep(spec.seconds * 1000);
        const frames = stats.measuring;
        stats.measuring = null;
        const s = summarise(frames);
        const a = atlas!.stats();
        pass.cases.push({
          id: spec.id,
          title: spec.title,
          spec: { id: spec.id, level: spec.level, batch: spec.batch, churn: spec.churn, cull: spec.cull, systems: spec.systems, seconds: spec.seconds },
          frames: frames.length,
          ...s,
          calls: stats.calls,
          triangles: stats.triangles,
          culled: stats.culled,
          instances: a.instances,
          session: { ...session },
          startedAt,
        });
        say(`result ${spec.id} (${mode}): avg ${s.avgMs.toFixed(2)} ms · p95 ${s.p95Ms.toFixed(2)} · draws ${stats.calls} · tris ${stats.triangles.toLocaleString()}`);
        if (mode === "2d") {
          stats.captureRequested = true;
          await sleep(120);
          if (stats.lastSnapshot) this.record.snapshots[spec.id] = stats.lastSnapshot;
          stats.lastSnapshot = null;
        }
      }
    } finally {
      pass.finishedAt = new Date().toISOString();
      this.record.passes.push(pass);
      this.record.log = [...log];
      this.current = null;
      this.running = false;
      if (mode === "xr") this.xrDone = true;
      saveLocal(this.record);
      (window as unknown as { __harnessResults?: unknown }).__harnessResults = this.record;
    }
    statusEl.textContent = mode === "xr" ? "XR pass done: exit VR to record what you saw and submit" : `2D pass done (${pass.cases.length} cases). Enter VR to run the XR pass.`;
    drawHud([`battery ${mode} done`, mode === "xr" ? "exit VR to submit" : "enter VR for the XR pass"]);
    if (mode === "2d") await this.send();
    return pass;
  }

  async send(): Promise<void> {
    this.record.log = [...log];
    this.record.submitted = await submit(this.record);
    saveLocal(this.record);
    say(`submitted: posted ${this.record.submitted.posted} · stored ${this.record.submitted.stored}${this.record.submitted.error ? ` · ${this.record.submitted.error}` : ""}`);
  }

  showForm(): void {
    formEl.hidden = false;
    const rows = this.cases.map((c) => `<label>${c.title} <select data-case="${c.id}"><option value="no">no flicker</option><option value="yes">flicker</option><option value="unsure">unsure</option></select></label>`).join("");
    formEl.innerHTML = `<b>What did you see?</b>${rows}
      <label>comfort <select id="comfort"><option value="fine">fine</option><option value="judder">judder</option><option value="unusable">unusable</option></select></label>
      <input id="tester" type="text" placeholder="your name"><textarea id="notes" rows="3" placeholder="notes"></textarea>
      <button id="submit">Submit results</button> <button id="copy">Copy JSON</button><div id="submitStatus"></div>`;
    formEl.querySelector<HTMLButtonElement>("#submit")!.addEventListener("click", async () => {
      const flicker: Observations["flicker"] = {};
      formEl.querySelectorAll<HTMLSelectElement>("select[data-case]").forEach((s) => (flicker[s.dataset.case!] = s.value as "no" | "yes" | "unsure"));
      this.record.observations = {
        flicker,
        comfort: formEl.querySelector<HTMLSelectElement>("#comfort")!.value as Observations["comfort"],
        notes: formEl.querySelector<HTMLTextAreaElement>("#notes")!.value,
        tester: formEl.querySelector<HTMLInputElement>("#tester")!.value,
      };
      await this.send();
      formEl.querySelector("#submitStatus")!.textContent = this.record.submitted.stored ? "stored on the server: the agent can collect it" : `posted ${this.record.submitted.posted}, stored ${this.record.submitted.stored}${this.record.submitted.error ? ` (${this.record.submitted.error})` : ""}; kept in this browser too`;
    });
    formEl.querySelector<HTMLButtonElement>("#copy")!.addEventListener("click", async () => {
      await navigator.clipboard.writeText(JSON.stringify(this.record));
      formEl.querySelector("#submitStatus")!.textContent = "copied";
    });
  }
}

let battery: Battery | null = null;

async function boot(): Promise<void> {
  world = await World.create(document.getElementById("scene-container") as HTMLDivElement, {
    xr: {
      sessionMode: SessionMode.ImmersiveVR,
      offer: "always",
      features: { handTracking: true, layers: true },
    },
    features: { locomotion: false, grabbing: false, physics: false, sceneUnderstanding: false, environmentRaycast: false, spatialUI: false },
  });
  const renderer = world.renderer;
  hookRenderer(renderer);
  const xr = renderer.xr as unknown as XrInternals;
  say(`webgl2 ${renderer.capabilities.isWebGL2} · WEBGL_multi_draw ${renderer.extensions.has("WEBGL_multi_draw")} · OCULUS_multiview ${renderer.extensions.has("OCULUS_multiview")} · three r${REVISION}`);

  xr.addEventListener("sessionstart", () => {
    const s = xr.getSession();
    const features = s?.enabledFeatures ? [...s.enabledFeatures] : [];
    session = { presenting: true, isMultiview: xr.isMultiview ?? null, layersGranted: s?.enabledFeatures ? features.includes("layers") : null, features, views: xr.getCamera()?.cameras?.length ?? null };
    say(`session start: isMultiview=${String(session.isMultiview)} · layers ${session.layersGranted === null ? "unknown" : session.layersGranted ? "granted" : "not granted"} · views ${session.views} · features ${features.join(",") || "unknown"}`);
    if (battery) void battery.run("xr").catch((e) => say(`xr pass failed: ${String(e)}`));
  });
  xr.addEventListener("sessionend", () => {
    session = { ...session, presenting: false };
    say("session end");
    if (battery && battery.record.passes.some((p) => p.mode === "xr")) battery.showForm();
  });

  world.scene.background = new Color("#22282e");
  world.camera.position.set(0, 1.5, 0);
  hudSprite = makeHud();
  world.camera.add(hudSprite);
  world.registerSystem(StatsSystem);

  say(`loading ${bundleBase}…`);
  try {
    manifest = await loadManifest(bundleBase, ports);
    await applyCase(manual);
    const s = atlas!.stats();
    (window as unknown as { __harness?: unknown }).__harness = { loaded: true, systems: s.systems, instances: s.instances, triangles: s.triangles, level: manual.level, stable: manual.batch === "stable", run: manifest.provenance.run };
    (window as unknown as { __harnessStats?: unknown }).__harnessStats = stats;
    say(`bundle ${manifest.id} run ${manifest.provenance.run.slice(0, 12)}: ${manifest.levels.map((l) => l.id).join("/")} · ${manifest.parts.length} parts`);
  } catch (error) {
    say(`bundle failed: ${error instanceof Error ? error.message : String(error)}`);
    (window as unknown as { __harness?: unknown }).__harness = { loaded: false, error: String(error) };
    return;
  }

  const startButton = document.getElementById("start") as HTMLButtonElement;
  const planSelect = document.getElementById("plan") as HTMLSelectElement;
  if (PLANS[planName]) planSelect.value = planName;
  const start = (plan: string) => {
    const cases = PLANS[plan];
    if (!cases) {
      say(`unknown battery ${plan}`);
      return;
    }
    battery = new Battery(plan, cases, renderer);
    startButton.disabled = true;
    void battery.run(session.presenting ? "xr" : "2d").catch((e) => say(`battery failed: ${String(e)}`));
  };
  startButton.addEventListener("click", () => start(planSelect.value));
  const previous = loadLocal();
  if (previous.length) say(`${previous.length} earlier result(s) in this browser; last submitted: posted ${previous[previous.length - 1]!.submitted.posted}`);
  if (planName !== "none" && (autoStart || !import.meta.env.DEV)) start(planName);
  else if (planName !== "none") say(`battery ${planName} ready: press Start (or add &auto=1)`);
}

void boot();
